import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip
} from '@mui/material';
import { format } from 'date-fns';
import { Search, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Close, Print as PrintIcon, FilterList } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { vendorInvoiceService } from '../services/vendorInvoiceService';
import { purchaseOrderService } from '../services/purchaseOrderService';
import { shipmentsInService } from '../services/shipmentsInService';
import { formatCurrency } from '../components/CurrencyTextField';
import ResizableHeaderCell from '../components/ResizableHeaderCell';
import { useResizableColumns } from '../hooks/useResizableColumns';
import usePersistedListFilters from '../hooks/usePersistedListFilters';
import { printWithFilename } from '../utils/printUtils';
import '../styles/print.css';
import PrintPage from '../components/print/PrintPage';
import {
  ProcurementSummaryBar,
  ProcurementSummaryField,
  ProcurementSummaryNotesDense
} from '../components/procurement/ProcurementSummaryBar';
import { formatDateOnly, formatLocalTodayYmd, sortTimestampFromDateOnly, parseLocalDate } from '../utils/dateUtils';

const VI_LIST_HEADER_FILTERS_INITIAL = {
  invoiceNumber: '',
  identifier: '',
  qbRef: '',
  vendor: '',
  po: '',
  receiver: '',
  invoiceDate: '',
  dueDate: '',
  amount: '',
  status: ''
};

const LIST_FILTERS_DEFAULTS = {
  searchTerm: '',
  headerColFilters: { ...VI_LIST_HEADER_FILTERS_INITIAL },
  sortKey: 'identifier',
  sortDirection: 'desc',
  page: 0
};

function trimHeaderFilterObj(obj) {
  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, String(v ?? '').trim()]));
}

function vendorInvoiceColumnFilterQueryParams(d) {
  const out = {};
  if (d.invoiceNumber) out.filter_invoice_number = d.invoiceNumber;
  if (d.identifier) out.filter_identifier = d.identifier;
  if (d.qbRef) out.filter_qb_ref = d.qbRef;
  if (d.vendor) out.filter_vendor = d.vendor;
  if (d.po) out.filter_po = d.po;
  if (d.receiver) out.filter_receiver = d.receiver;
  if (d.invoiceDate) out.filter_invoice_date = d.invoiceDate;
  if (d.dueDate) out.filter_due_date = d.dueDate;
  if (d.amount) out.filter_amount = d.amount;
  if (d.status) out.filter_status = d.status;
  return out;
}

const colHeaderStackSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 0.375,
  width: '100%',
  pr: 0
};

const colHeaderLabelSlotSx = {
  flexShrink: 0,
  width: '100%',
  minHeight: '1.5rem',
  overflow: 'hidden',
  '& .MuiTableSortLabel-root': {
    maxWidth: '100%',
    minWidth: 0,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.25,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.25,
    fontSize: '0.65rem',
    fontWeight: 700,
    '& .MuiTableSortLabel-icon': { flexShrink: 0 }
  }
};

const headerFilterTextFieldSx = {
  width: '100%',
  flexShrink: 0,
  '& .MuiInputBase-root': { fontSize: '0.7rem', py: 0.1 },
  '& .MuiInputBase-input': { py: 0.35 }
};

const emptyInvoiceForm = () => ({
  invoice_number: '',
  purchase_order_id: '',
  receiver_id: '',
  invoice_date: formatLocalTodayYmd(),
  due_date: '',
  total_amount: '',
  status: 'open',
  notes: '',
  quickbooks_ref: ''
});

function vendorInvoiceIsDueToday(inv) {
  const due = parseLocalDate(inv?.due_date);
  if (!due) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  return due.getTime() === today.getTime();
}

function vendorInvoiceIsPastDue(inv) {
  const due = parseLocalDate(inv?.due_date);
  if (!due) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  return due.getTime() < today.getTime();
}

/** Color-coded status chip for AP / vendor invoices (open / paid / closed + due cues). */
function vendorInvoiceStatusDisplay(inv) {
  const status = String(inv?.status || '').toLowerCase();
  if (status === 'paid') {
    return { label: 'Paid', sx: { bgcolor: 'success.main', color: '#fff' } };
  }
  if (status === 'closed') {
    return { label: 'Closed', sx: { bgcolor: 'grey.600', color: '#fff' } };
  }
  if (status === 'open' || !status) {
    if (vendorInvoiceIsPastDue(inv)) {
      return { label: 'Past Due', sx: { bgcolor: 'error.main', color: '#fff' } };
    }
    if (vendorInvoiceIsDueToday(inv)) {
      return { label: 'Due today', sx: { bgcolor: '#fdd835', color: '#111', fontSize: '0.68rem' } };
    }
    return { label: 'Open', sx: { bgcolor: 'info.main', color: '#fff' } };
  }
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  return { label, sx: { bgcolor: 'grey.500', color: '#fff' } };
}

function renderVendorInvoiceStatusChip(inv) {
  const main = vendorInvoiceStatusDisplay(inv);
  return (
    <Chip
      label={main.label}
      size="small"
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        height: 22,
        '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' },
        ...main.sx
      }}
    />
  );
}

const ProcurementInvoices = () => {
  const DEFAULT_COL_WIDTHS = { custInvNo: 130, identifier: 130, qbRef: 130, vendor: 200, poNo: 130, receiver: 130, date: 120, dueDate: 110, amount: 120, status: 120 };
  const [searchParams, setSearchParams] = useSearchParams();
  const poFilter = searchParams.get('po');
  const openNew = searchParams.get('openNew') === '1';
  const invoiceFilter = searchParams.get('invoice');
  const printRequested = searchParams.get('print') === '1';
  const {
    values: listFilters,
    setField: setListFilter,
    clear: clearListFilters,
    hasActiveFilters
  } = usePersistedListFilters('wm_vendor_invoices_list_filters', LIST_FILTERS_DEFAULTS);
  const searchTerm = listFilters.searchTerm;
  const setSearchTerm = useCallback((v) => setListFilter('searchTerm', v), [setListFilter]);
  const headerColFilters = listFilters.headerColFilters;
  const setHeaderColFilters = useCallback((v) => setListFilter('headerColFilters', v), [setListFilter]);
  const sortKey = listFilters.sortKey;
  const setSortKey = useCallback((v) => setListFilter('sortKey', v), [setListFilter]);
  const sortDirection = listFilters.sortDirection;
  const setSortDirection = useCallback((v) => setListFilter('sortDirection', v), [setListFilter]);
  const page = listFilters.page;
  const setPage = useCallback((v) => setListFilter('page', v), [setListFilter]);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(() => String(searchTerm || '').trim());
  const [debouncedHeaderColFilters, setDebouncedHeaderColFilters] = useState(() =>
    trimHeaderFilterObj(headerColFilters || VI_LIST_HEADER_FILTERS_INITIAL)
  );
  const handleClearFilters = useCallback(() => {
    clearListFilters();
    setDebouncedSearchTerm('');
    setDebouncedHeaderColFilters({ ...VI_LIST_HEADER_FILTERS_INITIAL });
    if (poFilter || invoiceFilter || openNew || printRequested) {
      setSearchParams({}, { replace: true });
    }
  }, [clearListFilters, poFilter, invoiceFilter, openNew, printRequested, setSearchParams]);
  const [invoices, setInvoices] = useState([]);
  const [invoicesTotal, setInvoicesTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const raw = parseInt(window.localStorage.getItem('wm_vendor_invoices_rows_per_page'), 10);
      return [50, 100, 200].includes(raw) ? raw : 50;
    } catch {
      return 50;
    }
  });
  const [loading, setLoading] = useState(true);
  const invoicesFilterKeyRef = useRef(null);
  const [error, setError] = useState('');
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState(emptyInvoiceForm);
  const [saving, setSaving] = useState(false);
  const [nextNoLoading, setNextNoLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [receiversByPo, setReceiversByPo] = useState([]);
  const hasAutoPrintedRef = useRef(false);
  const { colWidths, startResize } = useResizableColumns({
    defaultWidths: DEFAULT_COL_WIDTHS,
    storageKey: 'wm_procurement_invoices_col_widths',
    minWidths: { custInvNo: 90, identifier: 90, qbRef: 100, vendor: 150, poNo: 100, receiver: 100, date: 100, dueDate: 90, amount: 100, status: 100 }
  });

  const handleSort = (key) => {
    setSortDirection((prev) => (sortKey === key ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortKey(key);
  };

  const displayedInvoices = useMemo(() => {
    const list = [...invoices];
    const getter = (row) => {
      switch (sortKey) {
        case 'identifier': return row.identifier ?? '';
        case 'invoice_number': return row.invoice_number ?? '';
        case 'vendor': return row.vendor_name ?? '';
        case 'quickbooks_ref': return row.quickbooks_ref ?? '';
        case 'po_number': return row.po_identifier ?? row.po_number ?? '';
        case 'receiver_no': return row.receiver_identifier ?? row.receiver_no ?? '';
        case 'invoice_date': return sortTimestampFromDateOnly(row.invoice_date);
        case 'due_date': return sortTimestampFromDateOnly(row.due_date);
        case 'total_amount': return Number(row.total_amount) || 0;
        case 'status': return vendorInvoiceStatusDisplay(row).label;
        default: return '';
      }
    };
    const dir = sortDirection === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const va = getter(a);
      const vb = getter(b);
      if (typeof va === 'number' && typeof vb === 'number') return dir * (va - vb);
      return dir * String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    });
    return list;
  }, [invoices, sortKey, sortDirection]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    const t = setTimeout(() => {
      const next = trimHeaderFilterObj(headerColFilters);
      setDebouncedHeaderColFilters((prev) => {
        const same = Object.keys(VI_LIST_HEADER_FILTERS_INITIAL).every((k) => prev[k] === next[k]);
        return same ? prev : next;
      });
    }, 300);
    return () => clearTimeout(t);
  }, [headerColFilters]);

  const loadInvoices = useCallback(async () => {
    const filterKey = [
      debouncedSearchTerm,
      poFilter || '',
      invoiceFilter || '',
      JSON.stringify(debouncedHeaderColFilters)
    ].join('|');
    const filterJustChanged =
      invoicesFilterKeyRef.current != null && invoicesFilterKeyRef.current !== filterKey;
    invoicesFilterKeyRef.current = filterKey;

    const effectivePage = filterJustChanged ? 0 : page;
    if (filterJustChanged && page !== 0) {
      setPage(0);
    }

    try {
      setLoading(true);
      setError('');
      const params = {
        search: debouncedSearchTerm || undefined,
        purchase_order_id: poFilter || undefined,
        limit: rowsPerPage,
        offset: effectivePage * rowsPerPage,
        ...vendorInvoiceColumnFilterQueryParams(debouncedHeaderColFilters)
      };
      const res = await vendorInvoiceService.getAll(params);
      const list = res.data ?? [];
      const total = typeof res?.total === 'number' ? res.total : list.length;
      const maxPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
      if (effectivePage > maxPage) {
        setPage(maxPage);
        return;
      }
      setInvoicesTotal(total);
      setInvoices(list);
      if (invoiceFilter) {
        const matched = list.find((inv) => String(inv.id) === String(invoiceFilter));
        setSelectedInvoice(matched || (list.length > 0 ? list[0] : null));
      } else {
        setSelectedInvoice((prev) => {
          if (prev && list.some((inv) => inv.id === prev.id)) {
            return list.find((inv) => inv.id === prev.id) || list[0] || null;
          }
          return list.length > 0 ? list[0] : null;
        });
      }
    } catch (err) {
      console.error('Error loading vendor invoices:', err);
      setError(err.response?.data?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm, poFilter, invoiceFilter, debouncedHeaderColFilters, page, rowsPerPage]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  // When navigating from PO with ?po=X&openNew=1, open New Invoice dialog with PO pre-selected
  useEffect(() => {
    if (openNew && poFilter) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('openNew');
        return next;
      });
      openNewDialog(poFilter);
    }
  }, [openNew, poFilter]);

  useEffect(() => {
    if (!printRequested) return;
    if (loading) return;
    if (!selectedInvoice) return;
    if (hasAutoPrintedRef.current) return;
    hasAutoPrintedRef.current = true;
    const invoiceFilename = selectedInvoice?.identifier || selectedInvoice?.invoice_number || 'Vendor-Invoice';
    printWithFilename(invoiceFilename, { delayMs: 350 });
  }, [printRequested, loading, selectedInvoice]);

  useEffect(() => {
    if (!printRequested) hasAutoPrintedRef.current = false;
  }, [printRequested]);

  const loadPOsAndReceivers = async () => {
    try {
      const [poRes, recRes] = await Promise.all([
        purchaseOrderService.getAll({ limit: 500 }),
        shipmentsInService.getAll({ limit: 500 })
      ]);
      setPurchaseOrders(poRes?.data ?? []);
      setReceiversByPo(recRes?.data ?? []);
    } catch (err) {
      console.error('Error loading POs/receivers:', err);
    }
  };

  const handleSearch = () => {
    setDebouncedSearchTerm(searchTerm.trim());
  };

  const openNewDialog = async (preselectedPoId = null) => {
    setInvoiceForm(emptyInvoiceForm());
    setNewDialogOpen(true);
    setNextNoLoading(true);
    setError('');
    await loadPOsAndReceivers();
    try {
      const res = await vendorInvoiceService.getNextNumber();
      const next = res?.data?.invoice_number ?? `INV-${Date.now().toString(36).slice(-5).toUpperCase()}`;
      setInvoiceForm((f) => ({
        ...f,
        invoice_number: next,
        invoice_date: formatLocalTodayYmd(),
        purchase_order_id: preselectedPoId ? Number(preselectedPoId) : ''
      }));
    } catch (err) {
      setInvoiceForm((f) => ({
        ...f,
        invoice_date: formatLocalTodayYmd(),
        purchase_order_id: preselectedPoId ? Number(preselectedPoId) : ''
      }));
    } finally {
      setNextNoLoading(false);
    }
  };

  const handleCreateInvoice = async () => {
    const { invoice_number, purchase_order_id, invoice_date } = invoiceForm;
    if (!invoice_number?.trim()) {
      setError('Cust Invoice # is required.');
      return;
    }
    if (!purchase_order_id) {
      setError('Purchase order is required.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload = {
        invoice_number: invoice_number.trim(),
        purchase_order_id: Number(purchase_order_id),
        receiver_id: invoiceForm.receiver_id ? Number(invoiceForm.receiver_id) : null,
        invoice_date: invoice_date || formatLocalTodayYmd(),
        due_date: invoiceForm.due_date || null,
        total_amount: invoiceForm.total_amount !== '' && invoiceForm.total_amount != null ? Number(invoiceForm.total_amount) : null,
        status: invoiceForm.status || 'open',
        notes: invoiceForm.notes || null,
        quickbooks_ref: String(invoiceForm.quickbooks_ref || '').trim() || null
      };
      await vendorInvoiceService.create(payload);
      setNewDialogOpen(false);
      loadInvoices();
    } catch (err) {
      console.error('Error creating invoice:', err);
      setError(err.response?.data?.message || 'Failed to create invoice');
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = async (row) => {
    if (!row) return;
    setSelectedInvoice(row);
    setEditDialogOpen(true);
    setError('');
    await loadPOsAndReceivers();
    setInvoiceForm({
      invoice_number: row.invoice_number ?? '',
      purchase_order_id: row.purchase_order_id ?? '',
      receiver_id: row.receiver_id ?? '',
      invoice_date: (row.invoice_date || '').toString().slice(0, 10) || formatLocalTodayYmd(),
      due_date: (row.due_date || '').toString().slice(0, 10) || '',
      total_amount: row.total_amount ?? '',
      status: row.status ?? 'open',
      notes: row.notes ?? '',
      quickbooks_ref: row.quickbooks_ref ?? ''
    });
  };

  const handleUpdateInvoice = async () => {
    if (!selectedInvoice?.id) return;
    const { invoice_number, purchase_order_id, invoice_date } = invoiceForm;
    if (!invoice_number?.trim()) {
      setError('Cust Invoice # is required.');
      return;
    }
    if (!purchase_order_id) {
      setError('Purchase order is required.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload = {
        invoice_number: invoice_number.trim(),
        purchase_order_id: Number(purchase_order_id),
        receiver_id: invoiceForm.receiver_id ? Number(invoiceForm.receiver_id) : null,
        invoice_date: invoice_date || formatLocalTodayYmd(),
        due_date: invoiceForm.due_date || null,
        total_amount: invoiceForm.total_amount !== '' && invoiceForm.total_amount != null ? Number(invoiceForm.total_amount) : null,
        status: invoiceForm.status || 'open',
        notes: invoiceForm.notes || null,
        quickbooks_ref: String(invoiceForm.quickbooks_ref || '').trim() || null
      };
      await vendorInvoiceService.update(selectedInvoice.id, payload);
      setEditDialogOpen(false);
      setSelectedInvoice(null);
      loadInvoices();
    } catch (err) {
      console.error('Error updating invoice:', err);
      setError(err.response?.data?.message || 'Failed to update invoice');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteInvoice = async () => {
    if (!selectedInvoice?.id) return;
    try {
      setSaving(true);
      setError('');
      await vendorInvoiceService.delete(selectedInvoice.id);
      setDeleteConfirmOpen(false);
      setSelectedInvoice(null);
      loadInvoices();
    } catch (err) {
      console.error('Error deleting invoice:', err);
      setError(err.response?.data?.message || 'Failed to delete invoice');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (v) => formatDateOnly(v, 'MM/dd/yyyy') || '—';

  const receiversForSelectedPo = invoiceForm.purchase_order_id
    ? receiversByPo.filter((r) => Number(r.purchase_order_id) === Number(invoiceForm.purchase_order_id))
    : [];

  const receiverDisplay = (inv) =>
    inv?.receiver_identifier ?? inv?.receiver_no ?? inv?.receiver_name ?? (inv?.receiver_id ? `#${inv.receiver_id}` : '—');

  const poDisplay = (inv) => inv?.po_identifier ?? inv?.po_number ?? inv?.purchase_order_id ?? '—';

  const statusDisplay = (inv) => vendorInvoiceStatusDisplay(inv).label;

  const handlePrintInvoice = () => {
    if (!selectedInvoice) return;
    const name = selectedInvoice.identifier || selectedInvoice.invoice_number || `Vendor-Invoice-${selectedInvoice.id}`;
    printWithFilename(name, { delayMs: 350 });
  };

  return (
    <Box>
      <Box className="no-print-vendor-invoices">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Vendor Invoices
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" color="primary" size="small" startIcon={<PrintIcon />} onClick={handlePrintInvoice} disabled={!selectedInvoice}>
            Print
          </Button>
          <Button variant="contained" color="primary" size="small" startIcon={<EditIcon />} onClick={() => selectedInvoice && openEditDialog(selectedInvoice)} disabled={!selectedInvoice}>
            Edit
          </Button>
          <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} onClick={() => selectedInvoice && setDeleteConfirmOpen(true)} disabled={!selectedInvoice}>
            Delete
          </Button>
          <Button variant="contained" color="success" size="small" startIcon={<AddIcon />} onClick={openNewDialog}>
            New
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {poFilter && (
        <Alert severity="info" sx={{ mb: 1.5 }} onClose={handleClearFilters}>
          Showing invoices for P.O. #{poFilter}.{' '}
          <Button size="small" onClick={handleClearFilters}>Clear filters</Button>
        </Alert>
      )}

      <ProcurementSummaryBar secondaryRow={<ProcurementSummaryNotesDense text={selectedInvoice?.notes} />}>
        <ProcurementSummaryField label="Cust Invoice #">
          <Box sx={{ fontWeight: 600 }}>{selectedInvoice?.invoice_number ?? '—'}</Box>
        </ProcurementSummaryField>
        <ProcurementSummaryField label="Identifier">
          <Box sx={{ fontWeight: 600 }}>{selectedInvoice?.identifier ?? '—'}</Box>
        </ProcurementSummaryField>
        <ProcurementSummaryField label="QB Ref #">{selectedInvoice?.quickbooks_ref ?? '—'}</ProcurementSummaryField>
        <ProcurementSummaryField label="Vendor">{selectedInvoice?.vendor_name ?? '—'}</ProcurementSummaryField>
        <ProcurementSummaryField label="P.O. #">
          {poDisplay(selectedInvoice)}
        </ProcurementSummaryField>
        <ProcurementSummaryField label="Receiver">
          {receiverDisplay(selectedInvoice)}
        </ProcurementSummaryField>
        <ProcurementSummaryField label="Date">
          {selectedInvoice ? formatDate(selectedInvoice.invoice_date) : '—'}
        </ProcurementSummaryField>
        <ProcurementSummaryField label="Due">
          {selectedInvoice ? formatDate(selectedInvoice.due_date) : '—'}
        </ProcurementSummaryField>
        <ProcurementSummaryField label="Amount">
          {selectedInvoice ? formatCurrency(selectedInvoice.total_amount) : '—'}
        </ProcurementSummaryField>
        <ProcurementSummaryField label="Status">
          {selectedInvoice ? renderVendorInvoiceStatusChip(selectedInvoice) : '—'}
        </ProcurementSummaryField>
        <IconButton
          size="small"
          onClick={() => setSelectedInvoice(null)}
          disabled={!selectedInvoice}
          sx={{ ml: 'auto' }}
          title="Clear selection"
        >
          <Close fontSize="small" />
        </IconButton>
      </ProcurementSummaryBar>

      <Paper sx={{ p: 1, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by cust. invoice #, identifier, vendor, or P.O. number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {searchTerm ? (
                    <IconButton
                      size="small"
                      aria-label="Clear search"
                      onClick={() => setSearchTerm('')}
                      edge="end"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  ) : null}
                  <IconButton size="small" edge="end" onClick={handleSearch}>
                    <Search />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 260 }}
          />
          <Button variant="outlined" size="small" onClick={handleSearch}>
            Search
          </Button>
          {(hasActiveFilters || poFilter || invoiceFilter || printRequested || openNew) ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterList />}
              onClick={handleClearFilters}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Clear filters
            </Button>
          ) : null}
          <Typography variant="body2" sx={{ color: 'text.secondary', ml: { sm: 'auto' } }}>
            {invoicesTotal === 0
              ? 'No vendor invoices match the current filters.'
              : invoices.length === 0
                ? `Page ${page + 1} · ${invoicesTotal} invoice${invoicesTotal === 1 ? '' : 's'} total`
                : `Showing ${page * rowsPerPage + 1}–${page * rowsPerPage + invoices.length} of ${invoicesTotal} invoice${invoicesTotal === 1 ? '' : 's'}`}
          </Typography>
        </Box>
      </Paper>

      <TableContainer
        component={Paper}
        sx={{
          maxHeight: '60vh',
          overflowY: 'auto',
          overflowX: 'auto',
          scrollbarGutter: 'stable'
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{
            '& .MuiTableCell-head': { verticalAlign: 'top' },
            '& .MuiTableHead-root .MuiTableCell-stickyHeader': {
              top: -13,
              zIndex: 20,
              backgroundColor: 'background.paper',
              backgroundImage: 'none',
              backgroundClip: 'padding-box',
              opacity: 1,
              overflow: 'hidden',
              borderTop: '1px solid',
              borderTopColor: 'background.paper',
              boxShadow: (theme) =>
                `inset 0 -1px 0 rgba(224,224,224,0.9), 0 -13px 0 0 ${theme.palette.background.paper}`
            }
          }}
        >
          <TableHead>
            <TableRow>
              {[
                { key: 'custInvNo', filterKey: 'invoiceNumber', sortKeyName: 'invoice_number', label: 'Cust Invoice #' },
                { key: 'identifier', filterKey: 'identifier', sortKeyName: 'identifier', label: 'Identifier' },
                { key: 'qbRef', filterKey: 'qbRef', sortKeyName: 'quickbooks_ref', label: 'QB Ref #' },
                { key: 'vendor', filterKey: 'vendor', sortKeyName: 'vendor', label: 'Vendor' },
                { key: 'poNo', filterKey: 'po', sortKeyName: 'po_number', label: 'P.O. #' },
                { key: 'receiver', filterKey: 'receiver', sortKeyName: 'receiver_no', label: 'Receiver' },
                { key: 'date', filterKey: 'invoiceDate', sortKeyName: 'invoice_date', label: 'Date' },
                { key: 'dueDate', filterKey: 'dueDate', sortKeyName: 'due_date', label: 'Due' },
                { key: 'amount', filterKey: 'amount', sortKeyName: 'total_amount', label: 'Amount', align: 'right' },
                { key: 'status', filterKey: 'status', sortKeyName: 'status', label: 'Status' }
              ].map((col) => (
                <ResizableHeaderCell
                  key={col.key}
                  width={colWidths[col.key]}
                  align={col.align}
                  onResizeStart={(e) => startResize(col.key, e)}
                >
                  <Box sx={colHeaderStackSx}>
                    <Box sx={colHeaderLabelSlotSx}>
                      <TableSortLabel
                        active={sortKey === col.sortKeyName}
                        direction={sortDirection}
                        onClick={() => handleSort(col.sortKeyName)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </Box>
                    <TextField
                      size="small"
                      placeholder="Filter…"
                      value={headerColFilters[col.filterKey]}
                      onChange={(e) =>
                        setHeaderColFilters((p) => ({ ...p, [col.filterKey]: e.target.value }))
                      }
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      sx={headerFilterTextFieldSx}
                    />
                  </Box>
                </ResizableHeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No vendor invoices. Create one from Procurement → Invoices and link to a P.O. (and optionally a receiver) to mark receiving as billed.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              displayedInvoices.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  selected={selectedInvoice?.id === row.id}
                  onClick={() => setSelectedInvoice(row)}
                  onDoubleClick={() => openEditDialog(row)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ width: colWidths.custInvNo }}>{row.invoice_number ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.identifier }}>{row.identifier ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.qbRef }}>{row.quickbooks_ref ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.vendor }}>{row.vendor_name ?? '—'}</TableCell>
                  <TableCell sx={{ width: colWidths.poNo }}>{poDisplay(row)}</TableCell>
                  <TableCell sx={{ width: colWidths.receiver }}>{receiverDisplay(row)}</TableCell>
                  <TableCell sx={{ width: colWidths.date }}>{formatDate(row.invoice_date)}</TableCell>
                  <TableCell sx={{ width: colWidths.dueDate }}>{formatDate(row.due_date)}</TableCell>
                  <TableCell sx={{ width: colWidths.amount }} align="right">{formatCurrency(row.total_amount)}</TableCell>
                  <TableCell sx={{ width: colWidths.status }}>{renderVendorInvoiceStatusChip(row)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          rowsPerPageOptions={[50, 100, 200]}
          count={invoicesTotal}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            const next = parseInt(e.target.value, 10);
            setRowsPerPage(next);
            setPage(0);
            try {
              window.localStorage.setItem('wm_vendor_invoices_rows_per_page', String(next));
            } catch {
              // ignore
            }
          }}
          labelRowsPerPage="Rows per page"
        />
      </TableContainer>
      </Box>

      <PrintPage title="Vendor Invoice" showSpacer={false}>
        {selectedInvoice ? (
          <>
            <p style={{ fontSize: 12, marginBottom: 12, color: '#333' }}>
              Printed {format(new Date(), 'MM/dd/yyyy h:mm a')}
            </p>
            <table className="print-table print-table-no-zebra" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <tbody>
                <tr>
                  <th style={{ padding: 6, textAlign: 'left', width: '32%', verticalAlign: 'top' }}>Cust Invoice #</th>
                  <td style={{ padding: 6, verticalAlign: 'top' }}>{selectedInvoice.invoice_number ?? '—'}</td>
                </tr>
                <tr>
                  <th style={{ padding: 6, textAlign: 'left', verticalAlign: 'top' }}>Identifier</th>
                  <td style={{ padding: 6, verticalAlign: 'top' }}>{selectedInvoice.identifier ?? '—'}</td>
                </tr>
                <tr>
                  <th style={{ padding: 6, textAlign: 'left', verticalAlign: 'top' }}>QB Ref #</th>
                  <td style={{ padding: 6, verticalAlign: 'top' }}>{selectedInvoice.quickbooks_ref ?? '—'}</td>
                </tr>
                <tr>
                  <th style={{ padding: 6, textAlign: 'left', verticalAlign: 'top' }}>Vendor</th>
                  <td style={{ padding: 6, verticalAlign: 'top' }}>{selectedInvoice.vendor_name ?? '—'}</td>
                </tr>
                <tr>
                  <th style={{ padding: 6, textAlign: 'left', verticalAlign: 'top' }}>P.O. #</th>
                  <td style={{ padding: 6, verticalAlign: 'top' }}>{poDisplay(selectedInvoice)}</td>
                </tr>
                <tr>
                  <th style={{ padding: 6, textAlign: 'left', verticalAlign: 'top' }}>Receiver</th>
                  <td style={{ padding: 6, verticalAlign: 'top' }}>{receiverDisplay(selectedInvoice)}</td>
                </tr>
                <tr>
                  <th style={{ padding: 6, textAlign: 'left', verticalAlign: 'top' }}>Invoice date</th>
                  <td style={{ padding: 6, verticalAlign: 'top' }}>{formatDate(selectedInvoice.invoice_date)}</td>
                </tr>
                <tr>
                  <th style={{ padding: 6, textAlign: 'left', verticalAlign: 'top' }}>Due date</th>
                  <td style={{ padding: 6, verticalAlign: 'top' }}>{formatDate(selectedInvoice.due_date)}</td>
                </tr>
                <tr>
                  <th style={{ padding: 6, textAlign: 'left', verticalAlign: 'top' }}>Amount</th>
                  <td style={{ padding: 6, verticalAlign: 'top' }}>{formatCurrency(selectedInvoice.total_amount)}</td>
                </tr>
                <tr>
                  <th style={{ padding: 6, textAlign: 'left', verticalAlign: 'top' }}>Status</th>
                  <td style={{ padding: 6, verticalAlign: 'top' }}>{statusDisplay(selectedInvoice)}</td>
                </tr>
              </tbody>
            </table>
            {selectedInvoice.notes?.trim() ? (
              <div style={{ marginTop: 16, fontSize: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Notes</div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{selectedInvoice.notes}</div>
              </div>
            ) : null}
          </>
        ) : (
          <p style={{ fontSize: 12 }}>Select a vendor invoice, then use Print.</p>
        )}
      </PrintPage>

      {/* New invoice dialog */}
      <Dialog open={newDialogOpen} onClose={() => !saving && setNewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Vendor Invoice</DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Cust Invoice #"
                value={invoiceForm.invoice_number}
                onChange={(e) => setInvoiceForm((f) => ({ ...f, invoice_number: e.target.value }))}
                required
                disabled={nextNoLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="QB Ref #"
                value={invoiceForm.quickbooks_ref}
                onChange={(e) => setInvoiceForm((f) => ({ ...f, quickbooks_ref: e.target.value }))}
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Invoice date" type="date" value={invoiceForm.invoice_date} onChange={(e) => setInvoiceForm((f) => ({ ...f, invoice_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Due date" type="date" value={invoiceForm.due_date} onChange={(e) => setInvoiceForm((f) => ({ ...f, due_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Purchase order</InputLabel>
                <Select
                  label="Purchase order"
                  value={invoiceForm.purchase_order_id || ''}
                  onChange={(e) => setInvoiceForm((f) => ({ ...f, purchase_order_id: e.target.value, receiver_id: '' }))}
                >
                  <MenuItem value="">—</MenuItem>
                  {purchaseOrders.map((po) => (
                    <MenuItem key={po.id} value={po.id}>
                      {po.po_number} — {po.vendor_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Receiver (link to mark billed)</InputLabel>
                <Select
                  label="Receiver (link to mark billed)"
                  value={invoiceForm.receiver_id || ''}
                  onChange={(e) => setInvoiceForm((f) => ({ ...f, receiver_id: e.target.value }))}
                >
                  <MenuItem value="">—</MenuItem>
                  {receiversForSelectedPo.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.identifier ?? r.receiver_no} — {r.receiver_date ? formatDate(r.receiver_date) : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Total amount" type="number" inputProps={{ min: 0, step: 0.01 }} value={invoiceForm.total_amount ?? ''} onChange={(e) => setInvoiceForm((f) => ({ ...f, total_amount: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={invoiceForm.status} onChange={(e) => setInvoiceForm((f) => ({ ...f, status: e.target.value }))}>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Notes" multiline rows={2} value={invoiceForm.notes} onChange={(e) => setInvoiceForm((f) => ({ ...f, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateInvoice} disabled={saving || nextNoLoading}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit invoice dialog */}
      <Dialog open={editDialogOpen} onClose={() => !saving && (setEditDialogOpen(false), setSelectedInvoice(null))} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Vendor Invoice</DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Cust Invoice #" value={invoiceForm.invoice_number} onChange={(e) => setInvoiceForm((f) => ({ ...f, invoice_number: e.target.value }))} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="QB Ref #"
                value={invoiceForm.quickbooks_ref}
                onChange={(e) => setInvoiceForm((f) => ({ ...f, quickbooks_ref: e.target.value }))}
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Invoice date" type="date" value={invoiceForm.invoice_date} onChange={(e) => setInvoiceForm((f) => ({ ...f, invoice_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Due date" type="date" value={invoiceForm.due_date} onChange={(e) => setInvoiceForm((f) => ({ ...f, due_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Purchase order</InputLabel>
                <Select label="Purchase order" value={invoiceForm.purchase_order_id || ''} onChange={(e) => setInvoiceForm((f) => ({ ...f, purchase_order_id: e.target.value, receiver_id: '' }))}>
                  <MenuItem value="">—</MenuItem>
                  {purchaseOrders.map((po) => (
                    <MenuItem key={po.id} value={po.id}>
                      {po.po_number} — {po.vendor_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Receiver (link to mark billed)</InputLabel>
                <Select label="Receiver (link to mark billed)" value={invoiceForm.receiver_id || ''} onChange={(e) => setInvoiceForm((f) => ({ ...f, receiver_id: e.target.value }))}>
                  <MenuItem value="">—</MenuItem>
                  {receiversForSelectedPo.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.identifier ?? r.receiver_no} — {r.receiver_date ? formatDate(r.receiver_date) : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Total amount" type="number" inputProps={{ min: 0, step: 0.01 }} value={invoiceForm.total_amount ?? ''} onChange={(e) => setInvoiceForm((f) => ({ ...f, total_amount: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={invoiceForm.status} onChange={(e) => setInvoiceForm((f) => ({ ...f, status: e.target.value }))}>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Notes" multiline rows={2} value={invoiceForm.notes} onChange={(e) => setInvoiceForm((f) => ({ ...f, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDialogOpen(false); setSelectedInvoice(null); }} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateInvoice} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={deleteConfirmOpen} onClose={() => !saving && setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Vendor Invoice</DialogTitle>
        <DialogContent>
          <Typography>
            Delete invoice <strong>{selectedInvoice?.identifier ?? `#${selectedInvoice?.id}`}</strong>
            {selectedInvoice?.invoice_number ? (
              <> — Cust Invoice # <strong>{selectedInvoice.invoice_number}</strong></>
            ) : null}
            ? If linked to a receiver, that receiver will be marked not billed. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteInvoice} disabled={saving}>
            {saving ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProcurementInvoices;
